import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import SideBySide from 'components/Layout/SideBySide';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {Backend} from 'types/api';
import {DocumentType} from 'types/documents';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';

import usePatchDocumentConversionSettings from './services';
import {DocumentConversionOption, DocumentConversionSettings} from './types';

export type DocumentConversionSettingsBackend = Backend<{
    documentType: DocumentType;
    messages?: DocumentConversionOption;
    notes?: DocumentConversionOption;
    settingMid: string;
}>;

type DocumentConversionSettingsByTypeFormData =
    FormFields<DocumentConversionSettings>;

type FormOption = {label: string; value: DocumentConversionOption};

const DocumentConversionSettingsByType: FC = () => {
    const {data} = useBackend<DocumentConversionSettingsBackend>();

    const methods = useForm<DocumentConversionSettingsByTypeFormData>({
        defaultValues: {
            conversionMessage: data.messages,
            conversionNote: data.notes,
        },
    });
    const {handleSubmit} = methods;

    const {mutateAsync} = usePatchDocumentConversionSettings(data.settingMid);

    const options: FormOption[] = [
        {
            label: t`Leave Blank`,
            value: 'leave_blank',
        },
        {
            label: t`Use Preset`,
            value: 'copy_from_preset',
        },
        {
            label: t`Copy From Original`,
            value: 'copy_from_source',
        },
    ];

    const onSubmit = async (
        formData: DocumentConversionSettingsByTypeFormData,
    ) => {
        mutateAsync(formData)
            .then(
                onApiSuccess({
                    onRedirect: (response) => {
                        if (
                            response.name ===
                            'settings_document_conversion_view'
                        ) {
                            return setUrl(
                                window.Urls.settings_document_conversion_view(),
                            );
                        }
                        throw new Error('Unknown redirect');
                    },
                }),
            )
            .catch(onApiError(methods));
    };

    const documentTypeLabel = (): string => {
        let type: string;
        if (data.documentType === 'acceptance') {
            type = t`Acceptance Certificate`;
        } else if (data.documentType === 'deliveryslip') {
            type = t`Delivery Slip`;
        } else if (data.documentType === 'invoice') {
            type = t`Invoice`;
        } else if (data.documentType === 'order') {
            type = t`Order`;
        } else if (data.documentType === 'orderconfirmation') {
            type = t`Order Confirmation`;
        } else if (data.documentType === 'orderslip') {
            type = t`Order Slip`;
        } else if (data.documentType === 'paymentnotice') {
            type = t`Payment Notice`;
        } else if (data.documentType === 'quote') {
            type = t`Quote`;
        } else if (data.documentType === 'receipt') {
            type = t`Receipt`;
        } else if (data.documentType === 'timesheet') {
            type = t`Timesheet`;
        } else {
            throw new Error('Unknown document type');
        }
        return type;
    };
    const documentSubHeader = () => {
        const type = documentTypeLabel();
        const helptext = jt`How should the following fields be set when you convert to ${type}?`;
        return helptext;
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <NavigationTabs.DocumentSettings selection="conversionSettings" />
                <FormHeader
                    subtitle={documentSubHeader()}
                    title={documentTypeLabel()}
                />
                <SideBySide className="max-w-xl p-4">
                    {data.messages && (
                        <Radio
                            label={t`Messages`}
                            name="conversionMessage"
                            options={options}
                        />
                    )}
                    {data.notes && (
                        <Radio
                            label={t`Notes`}
                            name="conversionNote"
                            options={options}
                        />
                    )}
                    <FormResult />
                    <FormActions align="left">
                        <SubmitButton kind="primary">
                            {t`Update Settings`}
                        </SubmitButton>
                    </FormActions>
                </SideBySide>
            </form>
        </FormProvider>
    );
};

export default DocumentConversionSettingsByType;
